import { useEffect, useRef, useState } from 'react';
import { contactsApi } from '@/services/api';
import { formatCurrency } from '@/utils/currency';
import { formatDateOnly } from '@/utils/progress';
import { Loader2, Upload, X } from 'lucide-react';
import type { Contact } from '@/types';

/**
 * Editierbare Zellen des Rechnungs-Grids (Autosave je Zelle): Klick auf
 * die Zelle macht sie editierbar, Commit bei blur/Enter/change, Escape
 * revert. Fehler-/Speicher-Zustand kommt als Ring/Klasse von außen.
 */

/** Text-Zelle (Rechnungsnummer, Betreff) — Commit bei blur/Enter. */
export function TextCell({
  value, onSave, editable, saving, error, placeholder = '—', className = '',
}: {
  value: string;
  onSave: (next: string) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== value) onSave(next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="input py-1 text-sm w-full min-w-0"
      />
    );
  }
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={() => editable && setEditing(true)}
      title={error || (editable ? 'Klicken zum Bearbeiten' : undefined)}
      className={`block w-full min-w-0 truncate text-left text-sm py-1 px-1 rounded ${
        error ? 'ring-1 ring-red-500 bg-red-50' : editable ? 'hover:bg-gray-100 cursor-text' : ''
      } ${saving ? 'opacity-60' : ''} ${className}`}
    >
      {value || <span className="text-gray-400">{placeholder}</span>}
    </button>
  );
}

/** Zahl-Zelle (Beträge/Satz): de-DE-Anzeige, Commit bei blur/Enter. */
export function NumberCell({
  value, onSave, editable, saving, error, bold = false,
}: {
  value: string | number | null;
  onSave: (next: number | null) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
  bold?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value == null ? '' : String(value));
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const raw = draft.trim().replace(',', '.');
    const next = raw === '' ? null : parseFloat(raw);
    if (next == null || Number.isNaN(next)) {
      if (value != null) onSave(null);
      return;
    }
    if (value != null && Math.abs(Number(value) - next) < 0.005) return;
    onSave(next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="input py-1 text-sm text-right w-24"
      />
    );
  }
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={() => editable && setEditing(true)}
      title={error || (editable ? 'Klicken zum Bearbeiten' : undefined)}
      className={`block w-full text-right text-sm py-1 px-1 rounded tabular-nums ${
        bold ? 'font-semibold' : ''
      } ${error ? 'ring-1 ring-red-500 bg-red-50' : editable ? 'hover:bg-gray-100 cursor-text' : ''} ${
        saving ? 'opacity-60' : ''
      }`}
    >
      {value == null || value === '' ? <span className="text-gray-400">—</span> : formatCurrency(value)}
    </button>
  );
}

/** Datum-Zelle: ISO-Wireformat, Commit bei change. */
export function DateCell({
  value, onSave, editable, saving, error, danger = false,
}: {
  value: string | null;
  onSave: (next: string | null) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
  danger?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.showPicker?.();
  }, [editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={value ?? ''}
        onChange={(e) => {
          setEditing(false);
          const next = e.target.value || null;
          if (next !== (value ?? null)) onSave(next);
        }}
        onBlur={() => setEditing(false)}
        className="input py-1 text-sm w-32"
        autoFocus
      />
    );
  }
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={() => editable && setEditing(true)}
      title={error || (editable ? 'Klicken zum Bearbeiten' : undefined)}
      className={`block w-full text-left text-sm py-1 px-1 rounded whitespace-nowrap ${
        danger ? 'text-red-600 font-medium' : 'text-gray-900'
      } ${error ? 'ring-1 ring-red-500 bg-red-50' : editable ? 'hover:bg-gray-100' : ''} ${
        saving ? 'opacity-60' : ''
      }`}
    >
      {formatDateOnly(value) || <span className="text-gray-400">—</span>}
    </button>
  );
}

/** Select-Zelle (Bauvorhaben, Gewerk): natives <select>, kein Clipping. */
export function SelectCell({
  value, options, onSave, editable, saving, error, allowEmpty = true,
}: {
  value: number | null;
  options: Array<{ id: number; name: string }>;
  onSave: (next: number | null) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
  allowEmpty?: boolean;
}) {
  const selected = options.find((o) => o.id === value);
  if (!editable) {
    return <span className="block truncate text-sm text-gray-900 py-1 px-1">{selected?.name ?? <span className="text-gray-400">—</span>}</span>;
  }
  return (
    <select
      value={value ?? ''}
      onChange={(e) => {
        const next = e.target.value === '' ? null : Number(e.target.value);
        if (next !== value) onSave(next);
      }}
      className={`w-full rounded border border-transparent bg-transparent py-1 text-sm text-gray-900 hover:border-gray-300 focus:border-primary-500 focus:bg-white ${
        saving ? 'opacity-60' : ''
      } ${error ? 'ring-1 ring-red-500' : ''}`}
    >
      {allowEmpty && <option value="">— Keine —</option>}
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

/** Lieferant/Empfänger-Zelle: Suche im Dropdown (fixed positioniert — ein
 *  absolute Dropdown würde am overflow-Container des Grids geclippt). */
export function InvoiceContactCell({
  contactId, contactName, onSelect, editable, saving, error,
}: {
  contactId: number | null;
  contactName?: string | null;
  /** Zweiter Parameter = gewählter Kontakt (Drafts brauchen den Namen). */
  onSelect: (id: number | null, contact?: Contact) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!searchOpen) return;
    const anchor = buttonRef.current;
    if (anchor) setRect(anchor.getBoundingClientRect());
    let cancelled = false;
    const timer = setTimeout(() => {
      setIsLoading(true);
      contactsApi
        .getAll(1, term)
        .then((response) => {
          if (!cancelled) setResults(response.data.data.slice(0, 10));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchOpen, term]);

  if (!editable) {
    return (
      <span className="block truncate text-sm text-gray-900 py-1 px-1 max-w-[180px]" title={contactName || undefined}>
        {contactName || <span className="text-gray-400">—</span>}
      </span>
    );
  }

  return (
    <div className="relative inline-flex items-center gap-0.5 max-w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setSearchOpen(true);
          setTerm('');
        }}
        title={error || 'Lieferant/Empfänger wählen'}
        className={`min-w-0 max-w-[170px] truncate text-left text-sm py-1 px-1 rounded ${
          error ? 'ring-1 ring-red-500 bg-red-50' : 'hover:bg-gray-100'
        } ${saving ? 'opacity-60' : ''}`}
      >
        {contactName || <span className="text-gray-400">— wählen —</span>}
      </button>
      {contactId != null && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          title="Zuordnung entfernen"
          className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {searchOpen && rect && (
        <div
          className="fixed z-50 w-72 rounded-lg border border-gray-200 bg-white shadow-xl"
          style={{ top: rect.bottom + 2, left: Math.min(rect.left, window.innerWidth - 300) }}
        >
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchOpen(false);
            }}
            placeholder="Kontakt suchen…"
            className="w-full border-b border-gray-100 px-3 py-2 text-sm outline-none rounded-t-lg"
          />
          <div className="max-h-56 overflow-y-auto py-1">
            {isLoading && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            )}
            {!isLoading && results.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-500">Keine Treffer</p>
            )}
            {!isLoading && results.map((c) => {
              const name = c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || `Kontakt #${c.id}`;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onSelect(c.id, c);
                    setSearchOpen(false);
                  }}
                  className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-50"
                >
                  {name}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setSearchOpen(false)}
            className="w-full border-t border-gray-100 px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50"
          >
            Abbrechen
          </button>
        </div>
      )}
    </div>
  );
}

/** PDF-Zelle: Upload-Button (nur erfasst/korrektur_erforderlich, Whitelist
 *  uploadFile()); Klammersymbol, wenn eine Datei vorhanden ist. */
export function FileCell({
  hasFile, onUpload, editable, saving, error, onOpenDetail,
}: {
  hasFile: boolean;
  onUpload: (file: File) => void;
  editable: boolean;
  saving?: boolean;
  error?: string;
  onOpenDetail: () => void;
}) {
  if (hasFile && !editable) {
    return (
      <button type="button" onClick={onOpenDetail} title="PDF im Detail ansehen" className="rounded p-1 text-gray-500 hover:bg-gray-100">
        <Upload className="h-4 w-4" />
      </button>
    );
  }
  if (!editable) return <span className="block text-center text-sm text-gray-400">—</span>;
  return (
    <label
      title={error || 'PDF hochladen (max. 10 MB)'}
      className={`inline-flex cursor-pointer items-center justify-center rounded p-1 ${
        error ? 'bg-red-50 ring-1 ring-red-500' : 'hover:bg-gray-100'
      } ${saving ? 'opacity-60' : ''}`}
    >
      {saving ? (
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
      ) : (
        <Upload className={`h-4 w-4 ${hasFile ? 'text-primary-600' : 'text-gray-400'}`} />
      )}
      <input
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </label>
  );
}